/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

/**
 * Menu V3 RTK Query hooks
 * Partial menu API queries using v3 endpoints
 *
 * Uses custom axiosBaseQuery that wraps the existing axios client
 * Uses native infiniteQuery for pagination (RTK 2.5+)
 */

import { createApi } from "@reduxjs/toolkit/query/react"
import { InfiniteData } from "@reduxjs/toolkit/query"
import {
  FoodmaxapipbMenuV3ModifierGroupsResponse,
  FoodmaxapipbMenuV3HierarchiesResponse,
  FoodmaxapipbMenuV3ItemsResponse,
  FoodmaxapipbMenuV3EditorStatusResponse,
} from "generated/foodmaxapi.type"
import { axiosBaseQuery, createRequestHeaders } from "./apiClient"
import { apiReducerPaths, endpoints, CACHE_QUERY_TAGS } from "./constants"
import {
  MenuV3ModifierGroupsRequest,
  MenuV3HierarchiesRequest,
  MenuV3ItemsRequest,
  MenuV3EditorStatusRequest,
} from "./types"

// Export constants for use in components
export const DEFAULT_LIMIT = 100
export const DEFAULT_MODIFIER_GROUP_LIMIT = 50

// ============================================================================
// RTK Query API with Custom Axios Base Query
// ============================================================================

export const partialMenuApi = createApi({
  reducerPath: apiReducerPaths.partialMenuApi,
  baseQuery: axiosBaseQuery(),
  refetchOnMountOrArgChange: 300, // 5 minutes - refetch if data is older than 5 minutes
  // keepUnusedDataFor: defaults to 60 seconds - keep data in cache before removing it if no component is using it
  tagTypes: [
    CACHE_QUERY_TAGS.menuV3Items,
    CACHE_QUERY_TAGS.menuV3Hierarchies,
    CACHE_QUERY_TAGS.menuV3ModifierGroups,
  ],
  endpoints: builder => ({
    /**
     * Fetch Menu V3 Editor Status
     * Simple query - no pagination
     */
    menuV3EditorStatus: builder.query<
      FoodmaxapipbMenuV3EditorStatusResponse,
      MenuV3EditorStatusRequest
    >({
      query: ({ merchantID, merchantGroupID }) => ({
        url: endpoints.menuV3EditorStatus,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
      }),
    }),

    /**
     * Fetch Menu V3 Hierarchies with native infinite query
     */
    menuV3Hierarchies: builder.infiniteQuery<
      FoodmaxapipbMenuV3HierarchiesResponse, // Result type per page
      MenuV3HierarchiesRequest, // Query args (pageIndex handled by pageParam)
      number // PageParam type
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.hasMore ? lastPageParam + 1 : undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: endpoints.menuV3Hierarchies,
        headers: createRequestHeaders({
          merchantID: queryArg.merchantID,
          merchantGroupID: queryArg.merchantGroupID,
        }),
        params: {
          limit: queryArg.limit || DEFAULT_LIMIT,
          pageIndex: pageParam,
          parentType: queryArg.parentType,
          parentID: queryArg.parentID,
        },
      }),
    }),

    /**
     * Fetch Menu V3 Modifier Groups with native infinite query
     */
    menuV3ModifierGroups: builder.infiniteQuery<
      FoodmaxapipbMenuV3ModifierGroupsResponse,
      MenuV3ModifierGroupsRequest,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.hasMore ? lastPageParam + 1 : undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: endpoints.menuV3ModifierGroups,
        headers: createRequestHeaders({
          merchantID: queryArg.merchantID,
          merchantGroupID: queryArg.merchantGroupID,
        }),
        params: {
          limit: queryArg.limit || DEFAULT_MODIFIER_GROUP_LIMIT,
          pageIndex: pageParam,
        },
      }),
    }),

    /**
     * Fetch Menu V3 Items with native infinite query
     */
    menuV3Items: builder.infiniteQuery<
      FoodmaxapipbMenuV3ItemsResponse,
      MenuV3ItemsRequest,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.hasMore ? lastPageParam + 1 : undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: endpoints.menuV3Items,
        headers: createRequestHeaders({
          merchantID: queryArg.merchantID,
          merchantGroupID: queryArg.merchantGroupID,
        }),
        params: {
          limit: queryArg.limit || DEFAULT_LIMIT,
          pageIndex: pageParam,
          parentType: queryArg.parentType,
          parentID: queryArg.parentID,
          filter: queryArg.filter,
        },
      }),
      providesTags: [CACHE_QUERY_TAGS.menuV3Items],
    }),
  }),
})

// Export the API for store configuration

// ============================================================================
// Exported Hooks
// ============================================================================

export const {
  // Simple query
  useMenuV3EditorStatusQuery: useMenuV3EditorStatusRTK,
  // Native infinite queries (built-in fetchNextPage, hasNextPage, etc.)
  useMenuV3HierarchiesInfiniteQuery: useMenuV3HierarchiesInfinite,
  useMenuV3ModifierGroupsInfiniteQuery: useMenuV3ModifierGroupsInfinite,
  useMenuV3ItemsInfiniteQuery: useMenuV3ItemsInfinite,
} = partialMenuApi

// ============================================================================
// Helper to flatten pages (like React Query's data.pages.flat())
// ============================================================================

export function flattenInfiniteItems(
  data: InfiniteData<FoodmaxapipbMenuV3ItemsResponse, number> | undefined,
) {
  if (!data?.pages) return []
  return data.pages.flatMap(page => page.items || [])
}

export function flattenInfiniteHierarchies(
  data: InfiniteData<FoodmaxapipbMenuV3HierarchiesResponse, number> | undefined,
) {
  if (!data?.pages) return []
  return data.pages.flatMap(page => page.hierarchies || [])
}

export function flattenInfiniteModifierGroups(
  data:
    | InfiniteData<FoodmaxapipbMenuV3ModifierGroupsResponse, number>
    | undefined,
) {
  if (!data?.pages) return []
  return data.pages.flatMap(page => page.modifierGroups || [])
}
